/**
 * StarRating - Displays a numeric rating with star icons
 *
 * Matches the Figma Rating component (node 111:4919):
 *   - XS: 14px semibold value + 20px stars, gap-8px
 *   - Medium: 18px semibold value + 28px stars, gap-10px
 *   - Value color: text-dark-secondary
 *   - Filled stars: state-brand-active (#36d399)
 *   - Empty stars: text-dark-tertiary
 */

import { cn } from '@lib/cn';

export interface StarRatingProps {
  rating: number;
  maxStars?: number;
  size?: 'xs' | 'md';
  showValue?: boolean;
  className?: string;
}

// single Star icon
function StarIcon({ filled, size }: { filled: boolean; size: 'xs' | 'md' }) {
  return (
    <i
      className={cn(
        filled ? 'ri-star-fill' : 'ri-star-line',
        size === 'xs' ? 'text-[20px]' : 'text-[28px]',
        filled ? 'text-[--state-highlight-active]' : 'text-[--colors-alpha-dark-200]',
      )}
    />
  );
}

// Rating group: number + stars
export function StarRating({
  rating,
  maxStars = 5,
  size = 'md',
  showValue = true,
  className,
}: StarRatingProps) {
  const roundedRating = Math.round(rating);

  return (
    <div
      className={cn(
        'flex items-center',
        size === 'xs' ? 'gap-[--spacing-5]' : 'gap-[--spacing-6]',
        className,
      )}
      role="img"
      aria-label={`Rating: ${rating.toFixed(1)} out of ${maxStars} stars`}
    >
      {/* Rating Number */}
      {showValue && (
        <span
          className={cn(
            'font-semibold text-[--text-dark-secondary]',
            size === 'xs' ? 'text-sm ' : 'text-lg',
            size === 'xs' ? 'pt-[2px]' : 'pt-[4px]',
            className,
          )}
        >
          {rating.toFixed(1)}
        </span>
      )}
      {/* Rating Stars */}
      <div className={cn('flex items-center', size === 'xs' ? 'pb-[1px]' : 'pb-[2px]', className)}>
        {Array.from({ length: maxStars }, (_, i) => (
          <StarIcon key={i} filled={i < roundedRating} size={size} />
        ))}
      </div>
    </div>
  );
}
